import {APOLLO_OPTIONS} from 'apollo-angular';
import {HttpBatchLink, HttpLink} from 'apollo-angular/http';
import {ApolloClientOptions, ApolloLink, InMemoryCache, NormalizedCacheObject, Observable} from '@apollo/client/core';
import {setContext} from '@apollo/client/link/context';
import {ErrorResponse, onError} from '@apollo/client/link/error';
import {HttpHeaders} from '@angular/common/http';
import {InjectionToken, Provider} from '@angular/core';
import {AuthorizationError} from 'app/core/error-handler/errors/authorization.error';
import {LocalStorageService} from 'app/core/services/local-storage.service';
import {environment} from 'src/environments/environment';
import {ErrorFabricService} from '../error-handler/error-fabric.service';
import {ErrorNotifierService} from '../error-handler/error-notifier.service';
import possibleTypes from 'possibleTypes.json';
import {FetchResult} from '@apollo/client/link/core';
import {dataIdFromObject, typePolicies} from 'app/core/apollo/data-id-from-object';
import {persistCache} from 'apollo3-cache-persist';
import {boolDevFlag, DevFlags} from 'test-helpers/dev-flags';
import {Body} from 'apollo-angular/http/types';
import extractFiles from 'extract-files/extractFiles.mjs';
import isExtractableFile from 'extract-files/isExtractableFile.mjs';

export const BATCH_INTERVAL = 10; // ms
export const DEFAULT_APOLLO_LINK = new InjectionToken<ApolloLink>('defaultApolloLink');

export function createApollo(
    defaultApolloLink: ApolloLink,
    errorFactory: ErrorFabricService,
    errorNotifier: ErrorNotifierService,
    localStorage: LocalStorageService
): ApolloClientOptions<NormalizedCacheObject> {
    const errorLink = onError((errorResponse: ErrorResponse) => {
        const error = errorFactory.create(errorResponse);
        const {disableErrorNotifier} = errorResponse.operation.getContext();
        if (!disableErrorNotifier || error instanceof AuthorizationError) {
            errorNotifier.handleError(error);
        }
        if (error instanceof AuthorizationError) {
            localStorage.setItem('apd_access_token', '');
        }

        return new Observable<FetchResult>(sub => sub.error(error));
    });

    const requestHeaders = (context: {headers?: HttpHeaders; [p: string]: any}): HttpHeaders => {
        const headers = context.headers instanceof HttpHeaders ? context.headers : new HttpHeaders();
        context.headers = headers;
        return headers;
    };
    const auth = setContext((operation, context) => {
        // Get the authentication token from local storage if it exists
        const token = localStorage.getItem('apd_access_token');
        if (token) {
            return {
                headers: requestHeaders(context).set('Authorization', `Bearer ${token}`),
            };
        }
    });
    const traceContext = setContext((operation, context) => {
        if (context.traceId) {
            return {
                headers: requestHeaders(context).set('Trace-Id', `${context.traceId}`),
            };
        }
    });
    // await before instantiating ApolloClient, else queries might run before the cache is persisted
    // for instance fetch current user is never be cached - other queries - will
    const cache = new InMemoryCache({
        possibleTypes,
        typePolicies,
        dataIdFromObject,
    });

    if (boolDevFlag(DevFlags.EnableApolloPersistCache, false, localStorage)) {
        persistCache({
            cache,
            storage: localStorage,
            maxSize: false,
        } as any);
    }

    return {
        queryDeduplication: true,
        cache,
        link: ApolloLink.from([auth, traceContext, errorLink, defaultApolloLink]),
    };
}

function hasUploads(variables: Record<string, any> = {}) {
    return Object.values(variables).some(value => {
        if (value instanceof File || value instanceof FileList || value instanceof Blob) {
            return true;
        }
        if (value && typeof value === 'object') {
            return hasUploads(value);
        }
    });
}

export function defaultApolloLinkFactory(httpLink: HttpLink, batchLink: HttpBatchLink): ApolloLink {
    return ApolloLink.split(
        operation => operation.getContext().useMultipart || hasUploads(operation.variables) || operation.getContext().skipBatch,
        httpLink.create({
            uri: environment.graphQlEndpoint,
            extractFiles: (body: Body | Body[]) => extractFiles(body, isExtractableFile),
        }),
        batchLink.create({
            uri: environment.graphQlEndpoint,
            batchInterval: BATCH_INTERVAL,
        })
    );
}

export const apolloClientProvider: Provider = {
    provide: APOLLO_OPTIONS,
    useFactory: createApollo,
    deps: [DEFAULT_APOLLO_LINK, ErrorFabricService, ErrorNotifierService, LocalStorageService],
};
